/**
 * Progressive Disclosure Constants for Community Onboarding
 * Defines interest options and conditional questioning logic
 */

import {
  DollarSign,
  Shield,
  Target,
  Building,
  Settings,
  Rocket,
  MessageCircle,
  TrendingUp,
  Vote,
  Users,
  Zap,
} from 'lucide-react';
import type { TierType } from './tierConfiguration';

export interface InterestOption {
  id: string;
  label: string;
  tier: TierType;
  icon: React.ComponentType<{ className?: string; size?: number }>;
}

export interface ConditionalQuestion {
  id: string;
  label: string;
  type: 'select' | 'multiselect';
  options: string[];
}

export const INTEREST_OPTIONS: InterestOption[] = [
  // Silver tier interests - Basic community management and cost savings
  {
    id: 'cost-reduction',
    label: 'Reducing costs through group buying',
    tier: 'silver',
    icon: DollarSign,
  },
  {
    id: 'financial-management',
    label: 'Better financial management & treasury optimization',
    tier: 'silver',
    icon: Shield,
  },
  {
    id: 'vendor-management',
    label: 'Streamlined vendor management and coordination',
    tier: 'silver',
    icon: Building,
  },
  {
    id: 'communication-transparency',
    label: 'Improved communication and transparency',
    tier: 'silver',
    icon: MessageCircle,
  },
  {
    id: 'maintenance-facilities',
    label: 'Better maintenance and facilities management',
    tier: 'silver',
    icon: Settings,
  },

  // Gold tier interests - Advanced features and multi-community management
  {
    id: 'analytics-reporting',
    label: 'Advanced analytics and reporting',
    tier: 'gold',
    icon: Target,
  },
  {
    id: 'multi-property',
    label: 'Managing multiple properties or communities',
    tier: 'gold',
    icon: Building,
  },
  {
    id: 'investment-opportunities',
    label: 'Community investment and wealth building',
    tier: 'gold',
    icon: TrendingUp,
  },
  {
    id: 'governance-automation',
    label: 'Automated governance and voting systems',
    tier: 'gold',
    icon: Vote,
  },
  {
    id: 'resident-services',
    label: 'Enhanced resident services and amenities',
    tier: 'gold',
    icon: Users,
  },

  // Custom tier interests - Enterprise-level needs
  {
    id: 'custom-integrations',
    label: 'Custom integrations with existing systems',
    tier: 'custom',
    icon: Settings,
  },
  {
    id: 'infrastructure-projects',
    label: 'Large infrastructure projects & municipal bonds',
    tier: 'custom',
    icon: Rocket,
  },
  {
    id: 'municipal-partnerships-focus',
    label: 'Municipal government partnerships',
    tier: 'custom',
    icon: Building,
  },
  {
    id: 'enterprise-scaling',
    label: 'Enterprise-scale community management',
    tier: 'custom',
    icon: Zap,
  },
  {
    id: 'regulatory-compliance',
    label: 'Complex regulatory and compliance needs',
    tier: 'custom',
    icon: Shield,
  },
];

export const CONDITIONAL_QUESTIONS: Record<TierType, ConditionalQuestion[]> = {
  silver: [
    {
      id: 'treasury-balance',
      label: 'Current treasury balance range',
      type: 'select',
      options: ['Under $50K', '$50K-$200K', '$200K-$500K', '$500K-$1M', 'Over $1M'],
    },
    {
      id: 'vendor-pain-points',
      label: 'Biggest vendor management challenges',
      type: 'multiselect',
      options: [
        'Finding reliable vendors',
        'Coordinating schedules',
        'Price negotiations',
        'Quality control',
        'Payment processing',
      ],
    },
    {
      id: 'group-buying-interest',
      label: 'Group purchasing interest level',
      type: 'select',
      options: [
        'Very interested - want to start immediately',
        'Moderately interested',
        'Exploring options',
        'Not a priority',
      ],
    },
  ],
  gold: [
    {
      id: 'properties-managed',
      label: 'Number of properties managed',
      type: 'select',
      options: ['1 property', '2-3 properties', '4-10 properties', '10+ properties'],
    },
    {
      id: 'current-analytics',
      label: 'Current analytics tools used',
      type: 'multiselect',
      options: [
        'Excel/Google Sheets',
        'QuickBooks',
        'Property management software',
        'Custom dashboards',
        'None',
      ],
    },
    {
      id: 'automation-priorities',
      label: 'Top automation priorities',
      type: 'multiselect',
      options: [
        'Financial reporting',
        'Communication workflows',
        'Maintenance scheduling',
        'Vendor coordination',
        'Resident services',
      ],
    },
  ],
  custom: [
    {
      id: 'existing-systems',
      label: 'Existing systems requiring integration',
      type: 'multiselect',
      options: [
        'Banking/Financial systems',
        'Property management software',
        'Municipal systems',
        'Vendor platforms',
        'Custom databases',
      ],
    },
    {
      id: 'infrastructure-budget',
      label: 'Annual infrastructure budget',
      type: 'select',
      options: ['Under $100K', '$100K-$500K', '$500K-$1M', '$1M-$5M', 'Over $5M'],
    },
    {
      id: 'municipal-bonds',
      label: 'Experience with municipal bonds',
      type: 'select',
      options: [
        'Extensive experience',
        'Some experience',
        'Limited experience',
        'No experience but interested',
        'Not applicable',
      ],
    },
  ],
};

export const TIER_QUESTION_LABELS: Record<TierType, string> = {
  silver: '💰 Financial & Operations',
  gold: '⭐ Advanced Features',
  custom: '🚀 Custom Solutions',
};
